import React from 'react';
import * as Yup from 'yup';
import {Formik} from 'formik';
import ReCAPTCHA from 'react-google-recaptcha';
import {Box} from '../box';
import {Typography} from '../typography';
import {TextField} from '../text-field';
import {Checkbox} from '../checkbox';
import {Button} from '../button/button';
import {ArrowRightIcon} from '../icons/arrow-right';
import {VisibilityBox} from '../box/visibility-box';
import {HorizontalDivider} from '../divider';

export interface formValues {
  name: string;
  email: string;
  termsAccepted: boolean | null;
  recaptcha: string;
}

export interface EmailSignupFormProps {
  content1?: string | null;
  content2?: string | null;
  apiKey?: string;
  onSignupSubmit: (values: formValues) => void;
  ctaText?: string;
}

export const EmailSignupForm: React.FC<EmailSignupFormProps> = ({
  content1,
  content2,
  apiKey,
  onSignupSubmit,
  ctaText,
}) => {
  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Required'),
    email: Yup.string().email('Invalid email').required('Required'),
    termsAccepted: Yup.boolean().oneOf([true], 'This field must be checked'),
    recaptcha: Yup.string().required("Confirm you're not a robot"),
  });

  const initialValues = {
    name: '',
    email: '',
    termsAccepted: false,
    recaptcha: '',
  };

  return (
    <Box flex={1} justifyContent="space-between">
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSignupSubmit}
        validateOnChange
      >
        {({
          handleChange,
          handleBlur,
          handleSubmit,
          values,
          isSubmitting,
          setFieldValue,
          touched,
          errors,
        }) => {
          return (
            <Box justifyContent="space-between" flex={1}>
              <Box flex={1} m={['md', 'lg']}>
                <VisibilityBox show={!!content1 || !!content2}>
                  <VisibilityBox show={!!content1}>
                    <Box mb="four">
                      <Typography color="textDefault" textStyle="s">
                        {content1}
                      </Typography>
                    </Box>
                  </VisibilityBox>
                  <VisibilityBox show={!!content2}>
                    <Box mb="four">
                      <Typography color="textDefault" textStyle="s">
                        {content2}
                      </Typography>
                    </Box>
                  </VisibilityBox>
                </VisibilityBox>
                <Box marginBottom="four" maxWidth="448px">
                  <TextField
                    label="Your name"
                    placeholder="Full name"
                    onChangeText={handleChange('name')}
                    onBlur={handleBlur('name')}
                    value={values.name}
                    error={
                      errors.name && touched.name ? errors.name : undefined
                    }
                  />
                  <Box mt="lg">
                    <TextField
                      label="Your email"
                      placeholder="example@mail.com"
                      onChangeText={handleChange('email')}
                      onBlur={handleBlur('email')}
                      value={values.email}
                      error={
                        errors.email && touched.email ? errors.email : undefined
                      }
                    />
                  </Box>
                  <Checkbox
                    label="I agree to receive information on TokenTraxx"
                    checked={values.termsAccepted}
                    onCheckboxPress={() =>
                      setFieldValue('termsAccepted', !values.termsAccepted)
                    }
                    error={
                      errors.termsAccepted && touched.termsAccepted
                        ? errors.termsAccepted
                        : undefined
                    }
                  />
                  <Box mt="s">
                    <Typography textStyle="s" color="textLow">
                      You may unsubscribe at any time using the link in our
                      communications.
                    </Typography>
                  </Box>
                  {apiKey ? (
                    <Box mt="lg">
                      {/* @ts-expect-error react-google-recaptcha fix */}
                      <ReCAPTCHA
                        sitekey={apiKey}
                        onChange={(value) => setFieldValue('recaptcha', value)}
                        theme="dark"
                      />
                      {errors.recaptcha && touched.recaptcha && (
                        <Box mt="s">
                          <Typography color="errorPrimary" textStyle="s">
                            {errors.recaptcha}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  ) : null}
                </Box>
              </Box>
              <Box>
                <HorizontalDivider />
                <Button
                  type="primary"
                  disabled={isSubmitting}
                  onPress={handleSubmit}
                  leftAccessory={<ArrowRightIcon color="surfaceDark" />}
                >
                  {ctaText || 'Register'}
                </Button>
              </Box>
            </Box>
          );
        }}
      </Formik>
    </Box>
  );
};
